import { Component, Fragment, Suspense, createElement, useDeferredValue, useEffect, useMemo, useState } from "react";
import type { ComponentType, ErrorInfo, ReactElement, ReactNode } from "react";
import type { Anchor, Ref, Siblings } from "@nabla/api";
import type { Client } from "./client.ts";
import { clientCopy } from "./copy.ts";
import type { ClientError } from "./http.ts";
import { useHeld, viewState, type ViewState } from "./hooks.ts";
import type { ViewRequest } from "./request.ts";
import type { ViewEntry } from "./views.ts";

export type Level = "empresa" | "conta" | "campanha" | "conjunto" | "anuncio" | "mudanca" | "conversa";

export const depth: Readonly<Record<Level, number>> = { empresa: 0, conta: 1, campanha: 2, conjunto: 3, anuncio: 4, mudanca: 5, conversa: 5 };

type ParamsOf<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? { readonly [K in Name]: string } & ParamsOf<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? { readonly [K in Name]: string }
    : { readonly [K in never]: string };

export interface PlaceFacts {
  readonly title: string;
  readonly self?: Ref;
  readonly path: readonly Ref[];
  readonly siblings?: Siblings;
  readonly parent?: string;
  readonly managerUrl?: string | null;
}

export interface AskSlotProps {
  readonly anchor: Anchor;
  readonly name: string;
  readonly close: () => void;
}

export interface Slots {
  readonly answers?: ComponentType;
  readonly sidebar?: ComponentType;
  readonly ask?: ComponentType<AskSlotProps>;
}

export interface ViewProps<T> {
  readonly data: T;
  readonly stale: boolean;
  readonly params: Readonly<Record<string, string>>;
  readonly query: URLSearchParams;
}

interface ViewModule<T> {
  readonly default: ComponentType<ViewProps<T>>;
  readonly Loading?: ComponentType;
}

interface Common<P extends string> {
  readonly id: string;
  readonly path: P;
  readonly level?: Level;
  readonly public?: true;
  readonly morph?: (params: ParamsOf<P>) => string;
  readonly missing?: (params: ParamsOf<P>) => string;
  readonly replaceOn?: readonly string[];
}

type ViewLoader<T> = (opening: boolean) => Promise<ViewModule<T>>;

interface WithData<T, P extends string> extends Common<P> {
  readonly data: (params: ParamsOf<P>, query: URLSearchParams) => ViewRequest<T>;
  readonly view: ViewLoader<T>;
  readonly place?: (data: T) => PlaceFacts;
}

interface WithoutData<P extends string> extends Common<P> {
  readonly view: ViewLoader<undefined>;
  readonly place?: never;
}

type Rendered =
  | { readonly status: "carregando"; readonly Loading?: ComponentType }
  | { readonly status: "falhou" }
  | { readonly status: "erro"; readonly state: Extract<ViewState<unknown>, { status: "erro" }> }
  | { readonly status: "pronta"; readonly element: ReactElement; readonly facts?: PlaceFacts; readonly stale: boolean; readonly error?: ClientError };

export interface Opened {
  readonly key?: string;
  readonly morph?: string;
  readonly missing?: string;
  load(client: Client): Promise<void>;
  peek(client: Client): PlaceFacts | undefined;
  use(): Rendered;
}

export interface Entrypoint {
  readonly id: string;
  readonly path: string;
  readonly level?: Level;
  readonly public: boolean;
  readonly replaceOn: readonly string[];
  open(params: Readonly<Record<string, string>>, query: URLSearchParams): Opened;
}

export function entrypoint<T, const P extends string>(def: WithData<T, P>): Entrypoint;
export function entrypoint<const P extends string>(def: WithoutData<P>): Entrypoint;
export function entrypoint<T, const P extends string>(def: WithData<T, P> | WithoutData<P>): Entrypoint {
  return "data" in def ? withData(def) : withoutData(def);
}

type Loaded<M> = { readonly status: "carregando" | "falhou" } | { readonly status: "pronta"; readonly module: M };

export interface Code<M> {
  readonly now: () => Loaded<M>;
  readonly load: () => Promise<M>;
}

export function code<M>(load: () => Promise<M>): Code<M> {
  let now: Loaded<M> = { status: "carregando" };
  let loading: Promise<M> | undefined;
  return {
    now: () => now,
    load: () =>
      (loading ??= load().then(
        (module) => {
          now = { status: "pronta", module };
          return module;
        },
        (error: unknown) => {
          loading = undefined;
          now = { status: "falhou" };
          throw error;
        },
      )),
  };
}

class Unfetched {
  readonly retry: () => Promise<unknown>;
  constructor(retry: () => Promise<unknown>) {
    this.retry = retry;
  }
}

export function suspended<T>(load: () => Promise<T>): () => T {
  const loaded = code(load);
  return () => {
    const now = loaded.now();
    if (now.status === "pronta") return now.module;
    if (now.status === "falhou") throw new Unfetched(loaded.load);
    throw loaded.load().catch(() => undefined);
  };
}

let opened = false;
function opening(): boolean {
  const first = !opened;
  opened = true;
  return first;
}

interface Shown<T> {
  readonly entry: ViewEntry | undefined;
  readonly module: Loaded<ViewModule<T>>;
}

function useCode<M>(view: Code<M>, near = true): Loaded<M> {
  const [now, setNow] = useState(view.now);
  useEffect(() => {
    if (!near) return;
    const settled = () => setNow(view.now());
    void view.load().then(settled, settled);
  }, [near, view]);
  return now;
}

export function Unloaded(): ReactNode {
  return createElement("p", null, clientCopy.naoCarregou);
}

interface LoadsProps {
  readonly beside?: ReactNode;
  readonly broken?: (reset: () => void) => ReactNode;
  readonly report?: (error: unknown, info: ErrorInfo) => void;
  readonly children: ReactNode | (() => ReactNode);
}

const Said = ({ said }: { readonly said: () => ReactNode }): ReactNode => said();

interface Caught {
  readonly caught: unknown;
}

export class Loads extends Component<LoadsProps, Caught> {
  override state: Caught = { caught: undefined };
  readonly reset = () => this.setState({ caught: undefined });

  static getDerivedStateFromError(caught: unknown): Caught {
    return { caught };
  }

  override componentDidCatch(caught: unknown, info: ErrorInfo): void {
    if (caught instanceof Unfetched) void caught.retry().then(this.reset, () => undefined);
    else this.props.report?.(caught, info);
  }

  override render(): ReactNode {
    const { caught } = this.state;
    const { children } = this.props;
    if (caught === undefined) return typeof children === "function" ? createElement(Said, { said: children }) : children;
    if (caught instanceof Unfetched) return createElement(Fragment, null, createElement(Unloaded), this.props.beside);
    if (this.props.broken === undefined) throw caught;
    return this.props.broken(this.reset);
  }
}

export interface PartProps<M> {
  readonly parts: Code<M>;
  readonly near?: boolean;
  readonly waiting?: ReactNode;
  readonly children: (module: M) => ReactNode;
}

export function Part<M>({ parts, near = true, waiting = null, children }: PartProps<M>): ReactNode {
  const now = useCode(parts, near);
  if (now.status === "pronta") return createElement(Loads, null, createElement(Suspense, { fallback: null }, children(now.module)));
  return now.status === "falhou" ? createElement(Unloaded) : waiting;
}

function hasParams<P extends string>(params: Readonly<Record<string, string>>, path: P): params is Readonly<Record<string, string>> & ParamsOf<P> {
  return [...path.matchAll(/:(\w+)/g)].every((match) => typeof params[match[1] ?? ""] === "string");
}

function named<P extends string>(params: Readonly<Record<string, string>>, path: P): Readonly<Record<string, string>> & ParamsOf<P> {
  if (!hasParams(params, path)) throw new Error(`the router opened ${path} without its parameters`);
  return params;
}

function common<P extends string>(def: Common<P>): Omit<Entrypoint, "open"> {
  const level = def.level === undefined ? {} : { level: def.level };
  return { id: def.id, path: def.path, ...level, public: def.public === true, replaceOn: def.replaceOn ?? [] };
}

function withData<T, P extends string>(def: WithData<T, P>): Entrypoint {
  const view = code(() => def.view(opening()));
  return {
    ...common(def),
    open(raw, query) {
      const params = named(raw, def.path);
      const request = def.data(params, query);
      const facts = (data: T) => def.place?.(data);
      return {
        key: request.key,
        ...(def.morph ? { morph: def.morph(params) } : {}),
        ...(def.missing ? { missing: def.missing(params) } : {}),
        load: async (client) => {
          await Promise.all([client.views.load(request.key), view.load()]);
        },
        peek(client) {
          const held = client.views.entry(request.key)?.held;
          const read = held === undefined ? undefined : request.read(held);
          return read?.ok ? facts(read.value) : undefined;
        },
        use() {
          const entry = useHeld(request.key);
          const loaded = useCode(view);
          const now = useMemo((): Shown<T> => ({ entry, module: loaded }), [entry, loaded]);
          const shown = useDeferredValue(now, { entry: undefined, module: loaded });
          return useMemo((): Rendered => {
            if (shown.module.status === "falhou") return { status: "falhou" };
            const module = shown.module.status === "pronta" ? shown.module.module : undefined;
            const state = viewState(shown.entry, request.read);
            if (state.status === "erro") return { status: "erro", state };
            if (state.status === "carregando" || module === undefined) return module?.Loading ? { status: "carregando", Loading: module.Loading } : { status: "carregando" };
            const element = createElement(module.default, { data: state.data, stale: state.stale, params: raw, query });
            const placed = facts(state.data);
            return { status: "pronta", element, stale: state.stale, ...(placed ? { facts: placed } : {}), ...(state.error ? { error: state.error } : {}) };
          }, [shown]);
        },
      };
    },
  };
}

function withoutData<P extends string>(def: WithoutData<P>): Entrypoint {
  const view = code(() => def.view(opening()));
  return {
    ...common(def),
    open(raw, query) {
      return {
        ...(def.morph ? { morph: def.morph(named(raw, def.path)) } : {}),
        load: async () => {
          await view.load();
        },
        peek: () => undefined,
        use() {
          const loaded = useCode(view);
          return useMemo((): Rendered => (loaded.status !== "pronta" ? { status: loaded.status }
            : { status: "pronta", element: createElement(loaded.module.default, { data: undefined, stale: false, params: raw, query }), stale: false }), [loaded]);
        },
      };
    },
  };
}
